import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessToken, http } from './client';
import type {
  CertificateResults,
  Analytics,
  AppSettings,
  BoardData,
  CatalogTree,
  DashboardOverview,
  AuditEntry,
  GroupBreakdown,
  Invite,
  UserRow,
  Course,
  Direction,
  Flow,
  GenerateResult,
  Group,
  GroupDetail,
  GroupEnrollment,
  GroupLecturer,
  Lecturer,
  ImportOptions,
  ImportResult,
  Paginated,
  Project,
  GroupSummary,
  Session,
  SessionRoster,
  StudentCard,
  StudentDetail,
  StudentNote,
  StudentRow,
} from './types';

/** ცარიელი მნიშვნელობების მოცილება query string-იდან */
function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const keys = {
  projects: ['projects'] as const,
  flows: (projectId?: string) => ['flows', projectId ?? 'all'] as const,
  directions: ['directions'] as const,
  courses: (filters: Record<string, unknown>) => ['courses', filters] as const,
  tree: ['catalog-tree'] as const,
  groups: (filters: Record<string, unknown>) => ['groups', filters] as const,
  group: (id: string) => ['group', id] as const,
  lecturers: (courseId?: string) => ['lecturers', courseId ?? 'all'] as const,
};

// ============================================================
// კატალოგი
// ============================================================

export function useProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: () => http.get<Project[]>('/catalog/projects'),
  });
}

export function useFlows(projectId?: string) {
  return useQuery({
    queryKey: keys.flows(projectId),
    queryFn: () => http.get<Flow[]>(`/catalog/flows${qs({ projectId })}`),
  });
}

export function useDirections() {
  return useQuery({
    queryKey: keys.directions,
    queryFn: () => http.get<Direction[]>('/catalog/directions'),
  });
}

export function useCourses(
  filters: { directionId?: string; projectId?: string; lecturerId?: string } = {},
) {
  return useQuery({
    queryKey: keys.courses(filters),
    queryFn: () => http.get<Course[]>(`/catalog/courses${qs(filters)}`),
  });
}

export function useCatalogTree() {
  return useQuery({ queryKey: keys.tree, queryFn: () => http.get<CatalogTree>('/catalog/tree') });
}

/** კატალოგის ნებისმიერი ცვლილება ხეს და ჯგუფებსაც ეხება */
function useCatalogInvalidator() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.projects });
    void qc.invalidateQueries({ queryKey: ['flows'] });
    void qc.invalidateQueries({ queryKey: keys.directions });
    void qc.invalidateQueries({ queryKey: ['courses'] });
    void qc.invalidateQueries({ queryKey: keys.tree });
    void qc.invalidateQueries({ queryKey: ['groups'] });
  };
}

type Body = Record<string, unknown>;

export function useSaveProject() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id
        ? http.patch<Project>(`/catalog/projects/${id}`, data)
        : http.post<Project>('/catalog/projects', data),
    onSuccess: invalidate,
  });
}

export function useDeleteProject() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/catalog/projects/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveFlow() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id ? http.patch<Flow>(`/catalog/flows/${id}`, data) : http.post<Flow>('/catalog/flows', data),
    onSuccess: invalidate,
  });
}

export function useDeleteFlow() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/catalog/flows/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveDirection() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id
        ? http.patch<Direction>(`/catalog/directions/${id}`, data)
        : http.post<Direction>('/catalog/directions', data),
    onSuccess: invalidate,
  });
}

export function useDeleteDirection() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/catalog/directions/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveCourse() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id
        ? http.patch<Course>(`/catalog/courses/${id}`, data)
        : http.post<Course>('/catalog/courses', data),
    onSuccess: invalidate,
  });
}

export function useDeleteCourse() {
  const invalidate = useCatalogInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/catalog/courses/${id}`),
    onSuccess: invalidate,
  });
}

// ============================================================
// ჯგუფები
// ============================================================

export type GroupFilters = {
  flowId?: string;
  directionId?: string;
  courseId?: string;
  status?: string;
  projectId?: string;
  lecturerId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
};

export function useGroups(filters: GroupFilters) {
  return useQuery({
    queryKey: keys.groups(filters),
    queryFn: () => http.get<Paginated<Group>>(`/groups${qs(filters)}`),
  });
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: keys.group(id ?? ''),
    queryFn: () => http.get<GroupDetail>(`/groups/${id}`),
    enabled: Boolean(id),
  });
}

function useGroupInvalidator() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['groups'] });
    void qc.invalidateQueries({ queryKey: keys.tree });
    if (id) void qc.invalidateQueries({ queryKey: keys.group(id) });
  };
}

export function useSaveGroup() {
  const invalidate = useGroupInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id ? http.patch<Group>(`/groups/${id}`, data) : http.post<Group>('/groups', data),
    onSuccess: (group) => invalidate(group.id),
  });
}

export function useDeleteGroup() {
  const invalidate = useGroupInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/groups/${id}`),
    onSuccess: () => invalidate(),
  });
}

export function useAssignLecturer(groupId: string) {
  const invalidate = useGroupInvalidator();
  return useMutation({
    mutationFn: (data: { userId: string; role: string }) =>
      http.post<GroupLecturer>(`/groups/${groupId}/lecturers`, data),
    onSuccess: () => invalidate(groupId),
  });
}

export function useRemoveLecturer(groupId: string) {
  const invalidate = useGroupInvalidator();
  return useMutation({
    mutationFn: (userId: string) => http.del(`/groups/${groupId}/lecturers/${userId}`),
    onSuccess: () => invalidate(groupId),
  });
}

// ============================================================
// ლექტორები
// ============================================================

export function useLecturers(courseId?: string) {
  return useQuery({
    queryKey: keys.lecturers(courseId),
    queryFn: () => http.get<Lecturer[]>(`/users/lecturers${qs({ courseId })}`),
  });
}

// ============================================================
// სტუდენტები
// ============================================================

export type StudentFilters = {
  q?: string;
  groupId?: string;
  flowId?: string;
  projectId?: string;
  status?: string;
  unassigned?: string;
  page?: number;
  pageSize?: number;
};

export const studentKeys = {
  list: (f: StudentFilters) => ['students', f] as const,
  one: (id: string) => ['student', id] as const,
  groupEnrollments: (groupId: string) => ['enrollments', groupId] as const,
  sessions: (f: Record<string, unknown>) => ['sessions', f] as const,
};

export function useStudents(filters: StudentFilters, enabled = true) {
  return useQuery({
    queryKey: studentKeys.list(filters),
    queryFn: () => http.get<Paginated<StudentRow>>(`/students${qs(filters)}`),
    enabled,
  });
}

export function useStudent(id: string | undefined) {
  return useQuery({
    queryKey: studentKeys.one(id ?? ''),
    queryFn: () => http.get<StudentDetail>(`/students/${id}`),
    enabled: Boolean(id),
  });
}

function useStudentInvalidator() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['students'] });
    void qc.invalidateQueries({ queryKey: ['enrollments'] });
    void qc.invalidateQueries({ queryKey: ['groups'] });
    // ქარდის გასაღები ცალკეა — prefix „students" მას ვერ დაიჭერს და ეკრანი
    // ცვლილების შემდეგ ძველ სტატუსს აჩვენებდა
    void qc.invalidateQueries({ queryKey: ['student-card'] });
    if (id) void qc.invalidateQueries({ queryKey: studentKeys.one(id) });
  };
}

export function useSaveStudent() {
  const invalidate = useStudentInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Body }) =>
      id ? http.patch<StudentRow>(`/students/${id}`, data) : http.post<StudentRow>('/students', data),
    onSuccess: (s) => invalidate(s.id),
  });
}

export function useDeleteStudent() {
  const invalidate = useStudentInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/students/${id}`),
    onSuccess: () => invalidate(),
  });
}

// ============================================================
// ჩარიცხვები
// ============================================================

export function useGroupEnrollments(groupId: string | undefined) {
  return useQuery({
    queryKey: studentKeys.groupEnrollments(groupId ?? ''),
    queryFn: () => http.get<GroupEnrollment[]>(`/enrollments/group/${groupId}`),
    enabled: Boolean(groupId),
  });
}

export function useEnrollStudents(groupId: string) {
  const invalidate = useStudentInvalidator();
  return useMutation({
    mutationFn: (studentIds: string[]) =>
      http.post<{ added: number; skipped: number }>(`/enrollments/group/${groupId}`, { studentIds }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateEnrollment() {
  const invalidate = useStudentInvalidator();
  // სტატუსიც და პროექტიც დეშბორდის ჭრილებია — ანგარიშებიც უნდა განახლდეს
  const invalidateBoard = useBoardInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Body }) => http.patch(`/enrollments/${id}`, data),
    onSuccess: () => {
      invalidate();
      invalidateBoard();
    },
  });
}

export function useRemoveEnrollment() {
  const invalidate = useStudentInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/enrollments/${id}`),
    onSuccess: () => invalidate(),
  });
}

/**
 * სწავლის შეწყვეტა — ქარდის მოქმედება.
 * დაფის ფლაგისგან განსხვავებით ჩარიცხვას ხურავს, ამიტომ დეშბორდიც უნდა განახლდეს.
 */
export function useDropEnrollment() {
  const invalidateStudent = useStudentInvalidator();
  const invalidateBoard = useBoardInvalidator();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      http.post(`/enrollments/${id}/drop`, { reason }),
    onSuccess: () => {
      invalidateStudent();
      invalidateBoard();
    },
  });
}

export function useTransferEnrollment() {
  const invalidate = useStudentInvalidator();
  return useMutation({
    mutationFn: ({ id, targetGroupId }: { id: string; targetGroupId: string }) =>
      http.post(`/enrollments/${id}/transfer`, { targetGroupId, keepHistory: true }),
    onSuccess: () => invalidate(),
  });
}

// ============================================================
// გაკვეთილები
// ============================================================

export type SessionFilters = {
  groupId?: string;
  from?: string;
  to?: string;
  status?: string;
  mine?: string;
  limit?: number;
};

export function useSessions(filters: SessionFilters, enabled = true) {
  return useQuery({
    queryKey: studentKeys.sessions(filters),
    queryFn: () => http.get<Session[]>(`/sessions${qs(filters)}`),
    enabled,
  });
}

function useSessionInvalidator() {
  const qc = useQueryClient();
  return (groupId?: string) => {
    void qc.invalidateQueries({ queryKey: ['sessions'] });
    void qc.invalidateQueries({ queryKey: ['groups'] });
    // ჟურნალის `gate` გაკვეთილის მდგომარეობაზეა დამოკიდებული (თემა, სტატუსი,
    // თარიღი) — თემის დადასტურების შემდეგ აღრიცხვა მაშინვე უნდა გაიხსნას
    void qc.invalidateQueries({ queryKey: ['roster'] });
    if (groupId) void qc.invalidateQueries({ queryKey: keys.group(groupId) });
  };
}

export function useGenerateSessions(groupId: string) {
  const invalidate = useSessionInvalidator();
  return useMutation({
    mutationFn: (data: Body) => http.post<GenerateResult>(`/sessions/group/${groupId}/generate`, data),
    onSuccess: (res) => {
      if (!res.preview) invalidate(groupId);
    },
  });
}

/** ერთეული გაკვეთილის დამატება განრიგში (მაგ. აღდგენითი) */
export function useCreateSession(groupId: string) {
  const invalidate = useSessionInvalidator();
  return useMutation({
    mutationFn: (data: Body) => http.post<Session>(`/sessions/group/${groupId}`, data),
    onSuccess: () => invalidate(groupId),
  });
}

export function useUpdateSession(groupId?: string) {
  const invalidate = useSessionInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Body }) =>
      // `autoMarked` — რამდენი სტუდენტი მოინიშნა თემის დადასტურებისას
      http.patch<Session & { autoMarked?: number }>(`/sessions/${id}`, data),
    onSuccess: () => invalidate(groupId),
  });
}

export function useDeleteSession(groupId?: string) {
  const invalidate = useSessionInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/sessions/${id}`),
    onSuccess: () => invalidate(groupId),
  });
}

// ============================================================
// ჟურნალი / დასწრება
// ============================================================

export const attendanceKeys = {
  roster: (sessionId: string) => ['roster', sessionId] as const,
  summary: (groupId: string) => ['group-summary', groupId] as const,
};

export function useSessionRoster(sessionId: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.roster(sessionId ?? ''),
    queryFn: () => http.get<SessionRoster>(`/attendance/session/${sessionId}`),
    enabled: Boolean(sessionId),
  });
}

export function useGroupSummary(groupId: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.summary(groupId ?? ''),
    queryFn: () => http.get<GroupSummary>(`/attendance/group/${groupId}/summary`),
    enabled: Boolean(groupId),
  });
}

function useAttendanceInvalidator(sessionId: string, groupId?: string) {
  const qc = useQueryClient();
  return (data: SessionRoster) => {
    // სერვერის პასუხი უკვე შეიცავს ახალ მდგომარეობას — ქეშს პირდაპირ ვანახლებთ
    qc.setQueryData(attendanceKeys.roster(sessionId), data);
    void qc.invalidateQueries({ queryKey: ['sessions'] });
    void qc.invalidateQueries({ queryKey: ['groups'] });
    if (groupId) {
      void qc.invalidateQueries({ queryKey: attendanceKeys.summary(groupId) });
      void qc.invalidateQueries({ queryKey: keys.group(groupId) });
    }
  };
}

export function useSaveAttendance(sessionId: string, groupId?: string) {
  const invalidate = useAttendanceInvalidator(sessionId, groupId);
  return useMutation({
    mutationFn: (data: Body) => http.put<SessionRoster>(`/attendance/session/${sessionId}`, data),
    onSuccess: invalidate,
  });
}

export function useMarkOne(sessionId: string, groupId?: string) {
  const invalidate = useAttendanceInvalidator(sessionId, groupId);
  return useMutation({
    mutationFn: (data: { enrollmentId: string; status: string }) =>
      http.patch<SessionRoster>(`/attendance/session/${sessionId}/one`, data),
    onSuccess: invalidate,
  });
}

// ============================================================
// დეშბორდი / პარამეტრები / ფლაგები
// ============================================================

export type DashboardFilters = {
  /** პროექტის id; ცარიელი — ყველა პროექტი */
  projectId?: string;
  flowId?: string;
  riskThreshold?: number;
};

export const dashKeys = {
  settings: ['settings'] as const,
  overview: (f: DashboardFilters) => ['dash-overview', f] as const,
  groups: (f: DashboardFilters) => ['dash-groups', f] as const,
  board: (f: DashboardFilters) => ['dash-board', f] as const,
  analytics: (f: DashboardFilters) => ['dash-analytics', f] as const,
  certificates: (f: DashboardFilters) => ['dash-certificates', f] as const,
};

export function useSettings() {
  return useQuery({
    queryKey: dashKeys.settings,
    queryFn: () => http.get<AppSettings>('/settings'),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Body) => http.patch<AppSettings>('/settings', data),
    onSuccess: (s) => {
      qc.setQueryData(dashKeys.settings, s);
      void qc.invalidateQueries({ queryKey: ['dash-overview'] });
      void qc.invalidateQueries({ queryKey: ['dash-groups'] });
      void qc.invalidateQueries({ queryKey: ['dash-board'] });
      void qc.invalidateQueries({ queryKey: ['dash-analytics'] });
    },
  });
}

const cleanFilters = (f: DashboardFilters) => ({
  projectId: f.projectId || undefined,
  flowId: f.flowId || undefined,
  riskThreshold: f.riskThreshold,
});

export function useOverview(f: DashboardFilters) {
  return useQuery({
    queryKey: dashKeys.overview(f),
    queryFn: () => http.get<DashboardOverview>(`/dashboard/overview${qs(cleanFilters(f))}`),
  });
}

export function useGroupsBreakdown(f: DashboardFilters) {
  return useQuery({
    queryKey: dashKeys.groups(f),
    queryFn: () => http.get<GroupBreakdown[]>(`/dashboard/groups${qs(cleanFilters(f))}`),
  });
}

export function useBoard(f: DashboardFilters) {
  return useQuery({
    queryKey: dashKeys.board(f),
    queryFn: () => http.get<BoardData>(`/dashboard/board${qs(cleanFilters(f))}`),
  });
}

/** ნაკადის შედეგები — სერტიფიცირება მიმდინარე ეტაპზე */
export function useCertificateResults(f: DashboardFilters) {
  const { riskThreshold: _ignored, ...filters } = f;
  return useQuery({
    queryKey: dashKeys.certificates(filters),
    queryFn: () =>
      http.get<CertificateResults>(`/dashboard/certificates${qs(cleanFilters(filters))}`),
  });
}

export function useAnalytics(f: DashboardFilters) {
  return useQuery({
    queryKey: dashKeys.analytics(f),
    queryFn: () => http.get<Analytics>(`/dashboard/analytics${qs(cleanFilters(f))}`),
  });
}

function useBoardInvalidator() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['dash-board'] });
    void qc.invalidateQueries({ queryKey: ['dash-overview'] });
    void qc.invalidateQueries({ queryKey: ['dash-groups'] });
    void qc.invalidateQueries({ queryKey: ['dash-analytics'] });
    void qc.invalidateQueries({ queryKey: ['enrollments'] });
    void qc.invalidateQueries({ queryKey: ['students'] });
    // ქარდის გასაღები ცალკეა — prefix „student" მას ვერ დაიჭერს
    void qc.invalidateQueries({ queryKey: ['student-card'] });
    // სტატუსის კომენტარი ისტორიაშიც ჩაიწერა — ნაკადი უნდა განახლდეს
    void qc.invalidateQueries({ queryKey: ['student-notes'] });
  };
}

export function useSetFlag() {
  const invalidate = useBoardInvalidator();
  return useMutation({
    mutationFn: (data: { enrollmentId: string; flagType: string; note?: string }) =>
      http.put('/flags', data),
    onSuccess: invalidate,
  });
}

export function useRemoveFlag() {
  const invalidate = useBoardInvalidator();
  return useMutation({
    mutationFn: (enrollmentId: string) => http.del(`/flags/${enrollmentId}`),
    onSuccess: invalidate,
  });
}

// ============================================================
// სტუდენტის ქარდი / კომენტარები / ეტაპები
// ============================================================

export const cardKeys = {
  card: (id: string) => ['student-card', id] as const,
  notes: (id: string) => ['student-notes', id] as const,
};

export function useStudentCard(studentId: string | undefined) {
  return useQuery({
    queryKey: cardKeys.card(studentId ?? ''),
    queryFn: () => http.get<StudentCard>(`/students/${studentId}/card`),
    enabled: Boolean(studentId),
  });
}

export function useStudentNotes(studentId: string | undefined) {
  return useQuery({
    queryKey: cardKeys.notes(studentId ?? ''),
    queryFn: () => http.get<StudentNote[]>(`/assessments/notes/${studentId}`),
    enabled: Boolean(studentId),
  });
}

export function useCreateNote(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Body) => http.post<StudentNote>('/assessments/notes', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardKeys.notes(studentId) });
      void qc.invalidateQueries({ queryKey: ['dash-board'] });
    },
  });
}

export function useDeleteNote(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.del(`/assessments/notes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardKeys.notes(studentId) });
      void qc.invalidateQueries({ queryKey: ['dash-board'] });
    },
  });
}

export function useSaveStageResult(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Body) => http.put('/assessments/stage', data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: cardKeys.card(studentId) }),
  });
}

/** ეტაპის შეფასება ერთი ფორმით — დავალება და/ან პროექტი ერთ მოთხოვნაში */
export function useSaveStageSet(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Body) => http.put('/assessments/stage-set', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardKeys.card(studentId) });
      // სიებსა და „ნაკადის შედეგებში" სერტიფიკატი ჩანს
      void qc.invalidateQueries({ queryKey: ['students'] });
      void qc.invalidateQueries({ queryKey: ['enrollments'] });
      void qc.invalidateQueries({ queryKey: ['dash-certificates'] });
    },
  });
}

export function useDeleteStageResult(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.del(`/assessments/stage/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardKeys.card(studentId) });
      // სიებსა და „ნაკადის შედეგებში" სერტიფიკატი ჩანს
      void qc.invalidateQueries({ queryKey: ['students'] });
      void qc.invalidateQueries({ queryKey: ['enrollments'] });
      void qc.invalidateQueries({ queryKey: ['dash-certificates'] });
    },
  });
}

// ============================================================
// მომხმარებლები / აუდიტი / ექსპორტი
// ============================================================

export type UserFilters = {
  role?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
};

export const adminKeys = {
  users: (f: UserFilters) => ['users', f] as const,
  audit: (f: Record<string, unknown>) => ['audit', f] as const,
  auditEntities: ['audit-entities'] as const,
};

export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: adminKeys.users(filters),
    queryFn: () => http.get<Paginated<UserRow>>(`/users${qs(filters)}`),
  });
}

function useUserInvalidator() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['lecturers'] });
  };
}

export function useCreateUser() {
  const invalidate = useUserInvalidator();
  return useMutation({
    mutationFn: (data: Body) => http.post<{ user: UserRow; invite: Invite }>('/users', data),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useUserInvalidator();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Body }) => http.patch<UserRow>(`/users/${id}`, data),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useUserInvalidator();
  return useMutation({
    mutationFn: (id: string) => http.del(`/users/${id}`),
    onSuccess: invalidate,
  });
}

export function useCreateInvite() {
  const invalidate = useUserInvalidator();
  return useMutation({
    mutationFn: (userId: string) => http.post<Invite>(`/users/${userId}/invite`),
    onSuccess: invalidate,
  });
}

export type AuditFilters = {
  action?: string;
  entity?: string;
  actorId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export function useAudit(filters: AuditFilters) {
  return useQuery({
    queryKey: adminKeys.audit(filters),
    queryFn: () => http.get<Paginated<AuditEntry>>(`/audit${qs(filters)}`),
  });
}

export function useAuditEntities() {
  return useQuery({
    queryKey: adminKeys.auditEntities,
    queryFn: () => http.get<{ entity: string; count: number }[]>('/audit/entities'),
  });
}

/**
 * CSV ჩამოტვირთვა. `fetch`-ით მიდის, რადგან ბმულს Authorization header ვერ დაერთვება,
 * ხოლო access token მხოლოდ მეხსიერებაშია.
 */
export async function downloadCsv(path: string, filters: Record<string, string | undefined> = {}) {
  const res = await fetch(`/api/exports/${path}${qs(filters)}`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('ექსპორტი ვერ მოხერხდა');

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `${path}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ============================================================
// CSV იმპორტი
// ============================================================

/** მშრალი გაშვება — ბაზაში არაფერი იწერება */
export function useImportPreview() {
  return useMutation({
    mutationFn: (data: ImportOptions) => http.post<ImportResult>('/imports/preview', data),
  });
}

/** რეალური იმპორტი — ყველა სია იცვლება, ამიტომ ქეში სრულად ნულდება */
export function useImportCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ImportOptions) => http.post<ImportResult>('/imports/commit', data),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

// ============================================================
// მეილის ნაკადები (Microsoft Graph)
// ============================================================

export type EmailFlow = {
  id: string;
  event: string;
  senderEmail: string;
  isActive: boolean;
  subjectTemplate: string;
  bodyTemplate: string;
  conditions: Record<string, unknown>;
  schedule: string | null;
  description: string | null;
  updatedAt: string;
  updatedBy: { id: string; fullName: string } | null;
  availableVariables: string[];
  usedVariables: string[];
};

/** საიდუმლო აქ **არასოდეს** მოდის — მხოლოდ ის, ჩაწერილია თუ არა */
export type CredentialStatus = {
  tenantId: string | null;
  clientId: string | null;
  secretConfigured: boolean;
  secretSource: 'database' | 'env' | 'none';
  encryptionReady: boolean;
  keySource: 'explicit' | 'derived';
  updatedAt: string | null;
  updatedBy: { id: string; fullName: string } | null;
};

export type EmailLogRow = {
  id: string;
  event: string;
  recipient: string;
  senderEmail: string;
  subject: string;
  status: 'SENT' | 'FAILED' | 'SKIPPED';
  error: string | null;
  createdAt: string;
};

export type AbsenceRun = {
  event: string;
  active: boolean;
  candidates: number;
  sent: number;
  skipped: number;
  failed: number;
  preview?: { name: string; email: string; pct: number | null; streak: number }[];
  reason?: string;
};

const emailKeys = {
  flows: ['email-flows'] as const,
  credentials: ['email-credentials'] as const,
  logs: ['email-logs'] as const,
};

export function useEmailFlows() {
  return useQuery({
    queryKey: emailKeys.flows,
    queryFn: () => http.get<EmailFlow[]>('/email/flows'),
  });
}

export function useUpdateEmailFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<EmailFlow>) =>
      http.patch<EmailFlow>(`/email/flows/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: emailKeys.flows }),
  });
}

export function useEmailCredentials() {
  return useQuery({
    queryKey: emailKeys.credentials,
    queryFn: () => http.get<CredentialStatus>('/email/credentials'),
  });
}

export function useSaveEmailCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { tenantId?: string; clientId?: string; clientSecret?: string }) =>
      http.put<CredentialStatus>('/email/credentials', body),
    onSuccess: (s) => qc.setQueryData(emailKeys.credentials, s),
  });
}

export function useClearEmailSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.del<CredentialStatus>('/email/credentials/secret'),
    onSuccess: (s) => qc.setQueryData(emailKeys.credentials, s),
  });
}

/** ტოკენი მოდის? ყუთი ჩანს? — გაგზავნის გარეშე */
export function useVerifySender() {
  return useMutation({
    mutationFn: (sender: string) =>
      http.post<{
        ok: true;
        sender: string;
        tokenOk: true;
        mailboxChecked: boolean;
        displayName?: string;
        note?: string;
        grantedRoles: string[];
        canSend: boolean;
      }>('/email/verify', { sender }),
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      http.post<{ ok: true; to: string }>(`/email/flows/${id}/test`, { to }),
  });
}

/** dryRun — ვის მიუვიდოდა, გაგზავნის გარეშე */
export function useRunAbsenceFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      http.post<AbsenceRun>(`/email/flows/absence/run${dryRun ? '?dryRun=1' : ''}`),
    onSuccess: (_r, dryRun) => {
      if (!dryRun) void qc.invalidateQueries({ queryKey: emailKeys.logs });
    },
  });
}

export function useEmailLogs(event?: string) {
  return useQuery({
    queryKey: [...emailKeys.logs, event ?? 'all'],
    queryFn: () =>
      http.get<{ logs: EmailLogRow[]; schedules: string[] }>(
        `/email/logs${event ? `?event=${encodeURIComponent(event)}` : ''}`,
      ),
  });
}

// ============================================================
// SMS ნაკადები
// ============================================================

export type SmsFlow = {
  id: string;
  event: string;
  isActive: boolean;
  template: string;
  conditions: Record<string, unknown>;
  schedule: string | null;
  description: string | null;
  updatedAt: string;
  updatedBy: { id: string; fullName: string } | null;
  availableVariables: string[];
  usedVariables: string[];
  /** როგორ გამოიყურება ტექსტი სავალდებულო ბოლოსართით */
  preview: string;
};

export type SmsCredentialStatus = {
  baseUrl: string;
  publicKey: string | null;
  originator: string | null;
  suffix: string;
  privateKeyConfigured: boolean;
  ready: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; fullName: string } | null;
};

export type SmsLogRow = {
  id: string;
  event: string;
  recipient: string;
  text: string;
  status: 'SENT' | 'FAILED' | 'SKIPPED';
  error: string | null;
  createdAt: string;
};

export type AbsenceSmsRun = {
  event: string;
  active: boolean;
  candidates: number;
  sent: number;
  skipped: number;
  failed: number;
  preview?: { name: string; phone: string | null; group: string }[];
  reason?: string;
};

const smsKeys = {
  flows: ['sms-flows'] as const,
  credentials: ['sms-credentials'] as const,
  logs: ['sms-logs'] as const,
};

export function useSmsFlows() {
  return useQuery({ queryKey: smsKeys.flows, queryFn: () => http.get<SmsFlow[]>('/sms/flows') });
}

export function useUpdateSmsFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<SmsFlow>) =>
      http.patch<SmsFlow>(`/sms/flows/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: smsKeys.flows }),
  });
}

export function useSmsCredentials() {
  return useQuery({
    queryKey: smsKeys.credentials,
    queryFn: () => http.get<SmsCredentialStatus>('/sms/credentials'),
  });
}

export function useSaveSmsCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      baseUrl?: string;
      publicKey?: string;
      originator?: string;
      suffix?: string;
      privateKey?: string;
    }) => http.put<SmsCredentialStatus>('/sms/credentials', body),
    onSuccess: (s) => qc.setQueryData(smsKeys.credentials, s),
  });
}

export function useSendTestSms() {
  return useMutation({
    mutationFn: (body: { to: string; text?: string }) =>
      http.post<{ ok: true; to: string; providerRef: string | null; raw: string }>(
        '/sms/test',
        body,
      ),
  });
}

export function useRunAbsenceSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      http.post<AbsenceSmsRun>(`/sms/flows/absence/run${dryRun ? '?dryRun=1' : ''}`),
    onSuccess: (_r, dryRun) => {
      if (!dryRun) void qc.invalidateQueries({ queryKey: smsKeys.logs });
    },
  });
}

export function useSmsLogs() {
  return useQuery({
    queryKey: smsKeys.logs,
    queryFn: () => http.get<{ logs: SmsLogRow[] }>('/sms/logs'),
  });
}
